import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, timer, of, throwError, Subscription } from 'rxjs';
import { map, takeUntil, catchError, finalize, switchMap, tap } from 'rxjs/operators';
import { CourtSlotBookingRequestDto } from '../booking/booking-container/booking-container.component';
import { BookingApiService } from './booking-api.service';
import { CreateBookingHoldRequest } from '../models/booking-hold.model';
import { CreateBookingRequest, PaymentMethod } from '../models/booking.model';
import { TimeUtilsService } from './time-utils.service';

@Injectable({
  providedIn: 'root'
})
export class SlotHoldService implements OnDestroy {
  private heldSlot = new BehaviorSubject<CourtSlotBookingRequestDto | null>(null);
  private timeRemaining = new BehaviorSubject<number>(300); // 5 minutes in seconds
  private timerEnd$ = new Subject<void>();
  
  private holdExpiresAt: number | null = null;
  private holdTimeoutId: any = null;
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private errorSubject = new BehaviorSubject<string | null>(null);
  private subscriptions = new Subscription();
  
  // Add property for total hold duration
  private totalHoldDuration = new BehaviorSubject<number>(300); // Default 5 minutes
  
  // Add property for alternative court notification
  private alternativeCourtSelectedSubject = new BehaviorSubject<boolean>(false);

  constructor(
    private bookingApiService: BookingApiService,
    private timeUtils: TimeUtilsService
  ) {}

  /** 
   * Calls the server to hold the selected court slot
   * @param slot The slot to hold
   */
  holdSlot(slot: CourtSlotBookingRequestDto): void {

    console.log('Holding slot:', slot);
    
    // If there's already a held slot, release it first
    if (this.getCurrentHeldSlot() && this.getCurrentHeldSlot()?.id !== slot.id) {
      const releaseSubscription = this.releaseCurrentHold().subscribe(() => {
        this.createNewHold(slot).subscribe();
      });
      this.subscriptions.add(releaseSubscription);
    } else {
      const createSubscription = this.createNewHold(slot).subscribe();
      this.subscriptions.add(createSubscription);
    }
  }
  
  /**
   * Release the current hold (if any) via API call
   */
  private releaseCurrentHold(): Observable<void> {
    const currentHold = this.heldSlot.value;
    if (!currentHold) {
      return of(void 0);
    }
    
    const dateString = this.timeUtils.formatDateForApi(currentHold.date);
    const startTimeHHMM = currentHold.startTime.substring(0, 5); // Format: HH:mm
    
    return this.bookingApiService.releaseHold(
      currentHold.court.id, 
      dateString,
      startTimeHHMM
    ).pipe(
      tap(() => {
        this.stopCountdown();
        this.heldSlot.next(null);
        
        if (this.holdTimeoutId) {
          clearTimeout(this.holdTimeoutId);
          this.holdTimeoutId = null;
        }
      }),
      catchError(error => {
        console.error('Failed to release hold:', error);
        return of(void 0); // Continue with the flow even if release fails
      })
    );
  }
  
  /**
   * Create a new hold via API call
   */
  private createNewHold(slot: CourtSlotBookingRequestDto): Observable<void> {
    this.loadingSubject.next(true);
    this.errorSubject.next(null);
    
    // Prepare the request data
    const holdRequest: CreateBookingHoldRequest = {
      start_time: this.formatDateTimeForApi(slot.date, slot.startTime),
      end_time: this.formatDateTimeForApi(slot.date, slot.endTime)
    };
    
    // If we're targeting a specific court, use court_id
    if (slot.court?.id) {
      holdRequest.court_id = slot.court.id;
    } 
    // Otherwise use category_id
    else if (slot.category?.id) {
      holdRequest.category_id = slot.category.id;
    }

    console.log('Creating hold with request:', holdRequest);

    return this.bookingApiService.createHold(holdRequest).pipe(
      tap(response => {
        // Update the slot with actual court data from the response
        const updatedSlot: CourtSlotBookingRequestDto = {
          ...slot,
          id: response.hold_id, // Use the actual hold_id from the backend
          court: {
            id: response.court_id,
            name: response.court_name
          }
        };
        
        // Store the held slot with the updated data
        this.heldSlot.next(updatedSlot);
        
        // Use the server-provided expires_at (it's in seconds, JS needs milliseconds)
        this.holdExpiresAt = response.expires_at * 1000;
        
        // Calculate total hold duration (expires_at - current time)
        const now = Date.now();
        const totalDurationMs = this.holdExpiresAt - now;
        const totalDurationSecs = Math.ceil(totalDurationMs / 1000);
        
        // Store the total duration
        this.totalHoldDuration.next(totalDurationSecs);
        
        // Calculate and set the remaining time
        this.updateCountdown();
        
        // Set timeout to auto-release when expiration time is reached
        const timeUntilExpiry = Math.max(0, this.holdExpiresAt - Date.now());
        
        // Clear any existing timeout first to prevent memory leaks
        if (this.holdTimeoutId) {
          clearTimeout(this.holdTimeoutId);
        }
        
        this.holdTimeoutId = window.setTimeout(() => {
          this.releaseHeldSlot();
        }, timeUntilExpiry);
        
        // Start the countdown timer
        this.startCountdown();
        
        // Check if an alternative court was selected and show notification
        if (response.was_alternative) {
          this.alternativeCourtSelectedSubject.next(true);
          
          // Auto-hide notification after 5 seconds
          setTimeout(() => {
            this.alternativeCourtSelectedSubject.next(false);
          }, 5000);
        }
      }),
      catchError(error => {
        console.error('Failed to create hold:', error);
        // error.message on an HttpErrorResponse is the technical English
        // "Http failure response for …" string — never show that to users.
        const message =
          error?.status === 409
            ? 'Dieses Zeitfenster ist bereits belegt. Bitte wähle eine andere Zeit oder Dauer.'
            : 'Platz konnte nicht reserviert werden. Bitte versuche es erneut.';
        this.errorSubject.next(message);
        return throwError(() => error);
      }),
      finalize(() => {
        this.loadingSubject.next(false);
      }),
      map(() => void 0) // Convert to void Observable
    );
  }
  
  /**
   * Get the currently held slot
   */
  getHeldSlot(): Observable<CourtSlotBookingRequestDto | null> {
    return this.heldSlot.asObservable();
  }
  
  /**
   * Get the currently held slot (non-observable version)
   */
  getCurrentHeldSlot(): CourtSlotBookingRequestDto | null {
    return this.heldSlot.value;
  }
  
  /**
   * Get the remaining time in seconds
   */
  getTimeRemaining(): Observable<number> {
    return this.timeRemaining.asObservable();
  }
  
  /**
   * Get the formatted remaining time (mm:ss)
   */
  getFormattedTimeRemaining(): Observable<string> {
    return this.timeRemaining.pipe(
      map(seconds => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
      })
    );
  }
  
  /**
   * Get the loading state
   */
  getLoading(): Observable<boolean> {
    return this.loadingSubject.asObservable();
  }
  
  /**
   * Get any error messages
   */
  getError(): Observable<string | null> {
    return this.errorSubject.asObservable();
  }
  
  /**
   * Clear local hold state without making an API call.
   * Use after booking confirmation where the hold was already consumed server-side.
   */
  clearLocalHoldState(): void {
    this.stopCountdown();
    this.heldSlot.next(null);
    this.resetHoldStateSubjects();
    if (this.holdTimeoutId) {
      clearTimeout(this.holdTimeoutId);
      this.holdTimeoutId = null;
    }
  }

  /**
   * Reset all transient hold-related state to initial values so the next
   * subscriber to any of the BehaviorSubjects doesn't see stale data
   * (e.g. a low countdown number flashing in the modal).
   */
  private resetHoldStateSubjects(): void {
    this.holdExpiresAt = null;
    this.timeRemaining.next(300);
    this.totalHoldDuration.next(300);
    this.errorSubject.next(null);
    this.alternativeCourtSelectedSubject.next(false);
  }

  /**
   * Release the held slot
   */
  releaseHeldSlot(): void {
    if (this.heldSlot.value) {
      console.log('Releasing held slot:', this.heldSlot.value);
      
      const currentHold = this.heldSlot.value;
      const dateString = this.timeUtils.formatDateForApi(this.heldSlot.value.date);      
      const startTime = this.timeUtils.extractTimeForEndpoint(currentHold.startTime);

      
      console.log(`Releasing hold: courtId=${currentHold.court.id}, date=${dateString}, startTime=${startTime}`);

      this.bookingApiService.releaseHold(
        currentHold.court.id,
        dateString,
        startTime
      ).subscribe({
        next: () => {
          console.log('Hold released successfully');
          this.stopCountdown();
          this.heldSlot.next(null);
          this.resetHoldStateSubjects();
        },
        error: (error) => {
          console.error('Failed to release hold:', error);
          // Still clear the local state even if API call fails
          this.stopCountdown();
          this.heldSlot.next(null);
          this.resetHoldStateSubjects();
        }
      });
      
      // Clear the timeout if it exists
      if (this.holdTimeoutId) {
        clearTimeout(this.holdTimeoutId);
        this.holdTimeoutId = null;
      }
    }
  }
  
  /**
   * Confirm the booking with the backend
   * deprecated?
   */
  confirmBooking(userData: { userId: string; name: string; email: string, fuck: string }): void {
    if (!this.heldSlot.value) {
      this.errorSubject.next('Keine aktive Reservierung zum Bestätigen');
      return;
    }
    
    this.loadingSubject.next(true);
    
    // Simply use the ISO strings directly without reformatting
    const bookingData: CreateBookingRequest = {
      court_id: this.heldSlot.value.court.id,
      user_id: userData.userId,
      start_time: this.heldSlot.value.startTime,  // Direct use - already ISO format
      end_time: this.heldSlot.value.endTime,      // Direct use - already ISO format
      duration_minutes: this.heldSlot.value.durationMinutes,
      price: this.heldSlot.value.price,
      hold_id: this.heldSlot.value.id,
      client_time_zone: this.timeUtils.getClientTimeZone(),
      payment_method: PaymentMethod.ON_SITE
    };
    
    console.log('Sending booking confirmation with data:', bookingData);
    
    this.bookingApiService.confirmBooking(bookingData).subscribe({
      next: (response) => {
        this.loadingSubject.next(false);
        // Release the slot after successful booking
        this.stopCountdown();
        this.heldSlot.next(null);
        
        if (this.holdTimeoutId) {
          clearTimeout(this.holdTimeoutId);
          this.holdTimeoutId = null;
        }
      },
      error: (error) => {
        console.error('Booking confirmation failed:', error);
        this.errorSubject.next(error.message || 'Buchung konnte nicht bestätigt werden');
        this.loadingSubject.next(false);
      }
    });
  }
  
  /**
   * Updates the countdown value based on the server expiration time
   */
  private updateCountdown(): void {
    if (!this.holdExpiresAt) return;
    
    const now = Date.now();
    const remainingMs = Math.max(0, this.holdExpiresAt - now);
    const remainingSecs = Math.floor(remainingMs / 1000);
    
    this.timeRemaining.next(remainingSecs);
    
    // If time's up, release the hold
    if (remainingSecs <= 0) {
      this.releaseHeldSlot();
    }
  }
  
  /**
   * Starts a timer to update the countdown regularly
   */
  private startCountdown(): void {
    // First stop any existing countdown
    this.stopCountdown();
    
    // Start a new timer that updates every second
    const timer$ = timer(0, 1000).pipe(
      takeUntil(this.timerEnd$)
    );
    
    timer$.subscribe(() => {
      this.updateCountdown();
    });
  }
  
  /**
   * Stops the countdown timer
   */
  private stopCountdown(): void {
    this.timerEnd$.next();
  }
  
  
  /**
   * Format date and time for API (ISO format with timezone preservation)
   */
  private formatDateTimeForApi(date: Date, time: string): string {
    return this.timeUtils.formatDateTimeForApi(date, time);
  }
  
  /**
   * Get notification state when an alternative court was selected
   */
  getAlternativeCourtSelected(): Observable<boolean> {
    return this.alternativeCourtSelectedSubject.asObservable();
  }

  /**
   * Get the total hold duration in seconds
   */
  getTotalHoldDuration(): Observable<number> {
    return this.totalHoldDuration.asObservable();
  }
  
  /**
   * Get the current total hold duration (non-observable version)
   */
  getCurrentTotalHoldDuration(): number {
    return this.totalHoldDuration.value;
  }
  
  /**
   * Get the progress percentage of the countdown (0-100)
   */
  getTimerProgressPercentage(): Observable<number> {
    return this.timeRemaining.pipe(
      map(remaining => {
        const total = this.totalHoldDuration.value;
        if (total <= 0) return 0;
        const elapsed = total - remaining;
        return Math.min(Math.max((elapsed / total) * 100, 0), 100); // Clamp between 0-100
      })
    );
  }

  /** Clean up resources on destroy */
  ngOnDestroy(): void {
    // Clear all timeouts and intervals
    if (this.holdTimeoutId) {
      clearTimeout(this.holdTimeoutId);
      this.holdTimeoutId = null;
    }

    this.stopCountdown();

    // Unsubscribe from all subscriptions
    this.subscriptions.unsubscribe();

    // Clear any held slots
    this.heldSlot.next(null);
  }
}